import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Building2 } from "@/lib/public-icons";
import { useMemo, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { formatEmploymentType } from "@/lib/words/employment-type";
import { orpcPublic } from "@/lib/orpc";
import { OrganizationCareersAssistant } from "@/routes/careers/-components/organization-careers-assistant";
import { filterChoices } from "@/routes/careers/-components/filter-choices";
import { JobFilterSelect } from "@/routes/careers/-components/job-filter-select";
import { JobListingCard } from "@/routes/careers/-components/job-listing-card";
import { CareersHero } from "@/routes/careers/$slug/-components/careers-hero";
import { CareersNotFound } from "@/routes/careers/$slug/-components/careers-not-found";
import { SearchField } from "@/components/app/patterns/search-field";

export const Route = createFileRoute("/careers/org/$orgSlug/")({
  component: OrgCareersPage,
});

/** Every open position across an operator's buildings. The organization page has no single
 * facility, so it carries its own frame rather than the facility shell: there is no building to
 * name in the header and nowhere for an "open positions" link to point. */
function OrgCareersPage() {
  const { orgSlug } = Route.useParams();
  const [search, setSearch] = useState("");
  const [facility, setFacility] = useState("all");
  const [department, setDepartment] = useState("all");
  const [employment, setEmployment] = useState("all");

  const page = useQuery({
    ...orpcPublic.careers.organization.queryOptions({ input: { orgSlug } }),
    retry: false,
  });

  const jobs = useMemo(() => page.data?.jobs ?? [], [page.data?.jobs]);
  const facilityNames = useMemo(() => filterChoices(jobs.map((job) => job.facilityName)), [jobs]);
  const departments = useMemo(() => filterChoices(jobs.map((job) => job.department)), [jobs]);
  const employmentTypes = useMemo(
    () => filterChoices(jobs.map((job) => job.employmentType)),
    [jobs],
  );

  const shown = useMemo(() => {
    const query = search.trim().toLowerCase();
    return jobs.filter((job) => {
      if (facility !== "all" && job.facilityName !== facility) return false;
      if (department !== "all" && job.department !== department) return false;
      if (employment !== "all" && job.employmentType !== employment) return false;
      if (!query) return true;
      return [job.title, job.department, job.location, job.facilityName].some((field) =>
        field?.toLowerCase().includes(query),
      );
    });
  }, [jobs, search, facility, department, employment]);

  const brand = page.data?.organization;

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="border-b">
        <div className="mx-auto flex w-full max-w-5xl items-center gap-2 px-4 py-4 font-semibold">
          <Building2 className="text-primary size-5" />
          <span>{brand?.name ?? "Careers"}</span>
        </div>
      </header>

      <main className="flex-1">
        <div className="mx-auto w-full max-w-5xl px-4 py-8">
          {page.isError ? (
            <CareersNotFound />
          ) : (
            <>
              <CareersHero
                accentColor={brand?.accentColor}
                headline={brand?.headline}
                tagline={brand?.tagline}
              />

              <div className="mb-8">
                {page.isPending ? (
                  <Skeleton className="h-10 w-72" />
                ) : (
                  <h1 className="text-3xl font-semibold tracking-tight text-balance">
                    {/* The name wraps as one piece, so "Willow / Creek" never splits. */}
                    {brand?.name ? (
                      <>
                        Careers at <span className="inline-block">{brand.name}</span>
                      </>
                    ) : (
                      "Open positions"
                    )}
                  </h1>
                )}
                <p className="text-muted-foreground mt-3 max-w-2xl">
                  Every open position across our communities.
                </p>
              </div>

              <div className="mb-6 flex flex-wrap gap-3">
                <SearchField
                  className="min-w-60 flex-1"
                  placeholder="Search positions…"
                  value={search}
                  onChange={setSearch}
                />
                <JobFilterSelect
                  label="Filter by community"
                  allLabel="All communities"
                  values={facilityNames}
                  value={facility}
                  onChange={setFacility}
                />
                <JobFilterSelect
                  label="Filter by department"
                  allLabel="All departments"
                  values={departments}
                  value={department}
                  onChange={setDepartment}
                />
                <JobFilterSelect
                  label="Filter by employment type"
                  allLabel="All types"
                  values={employmentTypes}
                  value={employment}
                  onChange={setEmployment}
                  format={formatEmploymentType}
                />
              </div>

              {page.isPending ? (
                <div className="space-y-3">
                  <Skeleton className="h-20 w-full" />
                  <Skeleton className="h-20 w-full" />
                </div>
              ) : shown.length === 0 ? (
                <div className="text-muted-foreground py-12 text-center">
                  {jobs.length === 0
                    ? "No open positions at this time."
                    : "No positions match your search."}
                </div>
              ) : (
                <div className="space-y-3">
                  {shown.map((job) => (
                    <JobListingCard
                      key={job.id}
                      job={job}
                      careersSlug={job.careersSlug}
                      heading="h2"
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {/* Answers across every building, each labelled by the community it
              came from: homes differ on shifts and pay, and one averaged
              answer would be wrong about all of them. */}
          <div className="mt-10">
            <OrganizationCareersAssistant orgSlug={orgSlug} />
          </div>
        </div>
      </main>

      <footer className="border-t">
        <div className="text-muted-foreground mx-auto w-full max-w-5xl px-4 py-6 text-xs">
          {brand?.name ? `${brand.name}. Careers` : "Careers"}
        </div>
      </footer>
    </div>
  );
}
